import _ from 'lodash';
import fullOrgUrl from '../../utils/url/fullOrgUrl';
import postPath from '../../utils/url/postPath';
import EDITOR_INTEGRATION_MENTIONS from '../mentions/EditorIntegrationMentions';
import type { RawRule } from '../utils/UrlRule';
import UrlRule, { firstResult } from '../utils/UrlRule';
import safeParseUrl from '../../utils/url/safeParseUrl';
import matchEntityLinks from '../utils/matchEntityLink';

const DELIMITER = '/';

export interface EmbedValue {
  id: string;
  type: string;
  params?: string;
}
export type PreviewMode = 'fullScreen' | 'inline';

export const EmbedMode = { EDIT: 'EDIT', FULL: 'FULL', READONLY: 'READONLY' };

export function embedUrl(
  value: EmbedValue,
  mode = EmbedMode.EDIT,
  previewMode: PreviewMode = 'inline',
) {
  if (value.type === 'post') {
    return fullOrgUrl(
      postPath({
        id: value.id,
        title: '',
      }),
    );
  }
  const embed = EMBEDS[value.type];
  if (embed == null) {
    const mention = EDITOR_INTEGRATION_MENTIONS[value.type];
    if (mention?.buildUrl) {
      return mention.buildUrl(value.id);
    }
    return false;
  }
  const [id, ...otherSegments] = value.id.split(DELIMITER);
  const segments: IdSegments = [id, ...otherSegments];
  const params = value.params
    ? Object.fromEntries(new URLSearchParams(value.params))
    : {};
  if (mode === EmbedMode.FULL) {
    return embed.fullUrl(segments, params);
  }
  if (mode === EmbedMode.READONLY && embed.readUrl) {
    return embed.readUrl(segments, params);
  }
  return embed.url
    ? embed.url(segments, params, { previewMode })
    : embed.fullUrl(segments, params);
}

type IdSegments = [string | undefined, ...(string | undefined)[]];
type EmbedOptions = { previewMode: PreviewMode };
interface Rule {
  rules: UrlRule[];
  toIdSegments?: (
    captured: Record<string, string>,
    url: URL,
  ) => IdSegments | null;
  toParams?: (
    captured: Record<string, string>,
  ) => Record<string, string | null | undefined>;
  fullUrl: (segments: IdSegments, params: Record<string, string>) => string;
  url?: (
    segments: IdSegments,
    params: Record<string, string>,
    options: EmbedOptions,
  ) => string;
  readUrl?: (segments: IdSegments, params: Record<string, string>) => string;
  defaultAttributes?: Record<string, unknown>;
}

const getGoogleFileConfigs = (
  type: string,
  overrideRules: Partial<RawRule> = {},
): Rule => ({
  rules: [
    new UrlRule({
      host: 'docs.google.com',
      path: `{/a/:_domain}?/${type}{/u/:_uid}?/d/:e(e)?/:id`,
      ...overrideRules,
    }),
  ],
  toIdSegments: ({ e, id }) => (e ? [e, id] : [id]),
  fullUrl: (segments) =>
    `https://docs.google.com/${type}/d/${segments.join('/')}`,
});

export const EMBEDS: {
  [key: string]: Rule;
} = {
  airtable: {
    rules: [
      new UrlRule({
        host: 'airtable.com',
        path: '/:_(embed)?{/:app(app[\\w-]+)}?/:id(shr[\\w-]+){/:tbl(tbl[\\w-]+)}?',
        queries: {
          layout: ':layout?',
          viewControls: ':viewControls?',
        },
      }),
    ],
    toIdSegments: ({ id, app }) => (app ? [id, app] : [id]),
    toParams: ({ tbl, layout, viewControls }) => ({
      tbl,
      layout,
      viewControls,
    }),
    fullUrl: ([id, app], params) =>
      id
        ? `https://airtable.com/${app ? `${app}/` : ''}${id}${
            params.tbl ? `/${params.tbl}` : ''
          }`
        : '',
    url: ([id, app], { tbl, ...params }) => {
      const search = new URLSearchParams(params).toString();
      return id
        ? `https://airtable.com/embed/${app ? `${app}/` : ''}${id}${
            tbl ? `/${tbl}` : ''
          }${search ? `?${search}` : ''}`
        : '';
    },
  },
  figma: ((): Rule => {
    const baseRule = new UrlRule({
      host: 'www.figma.com',
      path: '/:type(design|file|proto)/:id',
      queries: { 'node-id': ':frame?' },
    });
    const fullUrl: Rule['fullUrl'] = ([id, frame], { type }) => {
      const base = id ? `https://www.figma.com/${type ?? 'file'}/${id}` : '';
      return frame ? `${base}?node-id=${frame}` : base;
    };
    return {
      rules: [
        baseRule,
        new UrlRule({
          host: 'www.figma.com',
          path: '/embed',
          queries: { url: ':url' },
        }),
      ],
      toParams: ({ type }) => (type === 'file' ? {} : { type }),
      toIdSegments: ({ url, ...captured }) => {
        let { id, frame } = captured;
        if (url) {
          const parsed = safeParseUrl(url);
          const matched = parsed && baseRule.match(parsed);
          if (!matched) return null;
          id = matched.id;
          frame = matched.frame;
        }
        return frame ? [id, encodeURIComponent(frame)] : [id];
      },
      fullUrl,
      url: (segments, params) => {
        const dest = encodeURIComponent(fullUrl(segments, params));
        return `https://www.figma.com/embed?embed_host=share&url=${dest}`;
      },
    };
  })(),
  gliffy: {
    rules: [
      new UrlRule({ host: 'go.gliffy.com', path: '/go/share/image/:id.png' }),
    ],
    fullUrl: ([id]) =>
      id ? `https://go.gliffy.com/go/share/image/${id}.png` : '',
  },
  'google-calendar': ((): Rule => {
    const getUrl = (
      [id, params]: IdSegments,
      baseURL: string,
      paramName: string,
    ) => {
      if (!id) return '';
      const url = new URL(baseURL);
      const searchParams = new URLSearchParams(params);
      id.split(',').forEach((part) => {
        try {
          searchParams.append(paramName, decodeURIComponent(part));
        } catch (e) {
          // ignore URI malformed inputs
        }
      });
      url.search = searchParams.toString();
      return url.toString();
    };
    return {
      rules: [
        new UrlRule({
          host: 'calendar.google.com',
          path: '/calendar{/u/:_uid}?/:_type(embed|htmlembed)',
          queries: {
            src: ':_id',
            showTitle: ':showTitle?',
            showNav: ':showNav?',
            showDate: ':showDate?',
            showPrint: ':showPrint?',
            showTabs: ':showTabs?',
            showTz: ':showTz?',
            mode: ':mode?', // Week, Month, Agenda
            wkst: ':wkst?', // Week starts on
            hl: ':hl?', // Language
            ctz: ':ctz?', // Timezone
          },
        }),
      ],
      toIdSegments: (args, url) => {
        const src = url.searchParams.getAll('src');
        if (!src.length) return null;
        const params = new URLSearchParams(
          _.omit(args, ['_type', '_uid', '_id']),
        ).toString();
        return [src.map(encodeURIComponent).join(','), params];
      },
      fullUrl: ([id, params]) =>
        getUrl([id, params], 'https://calendar.google.com/calendar/r', 'cid'),
      url: ([id, params]) =>
        getUrl(
          [id, params],
          'https://calendar.google.com/calendar/embed',
          'src',
        ),
    };
  })(),
  'google-doc': {
    ...getGoogleFileConfigs('document'),
    readUrl: (segments) =>
      `https://docs.google.com/document/d/${segments.join('/')}/preview`,
    url: (segments) =>
      `https://docs.google.com/document/d/${segments.join(
        '/',
      )}/edit?rm=minimal`,
  },
  'google-drawing': {
    ...getGoogleFileConfigs('drawings'),
    url: (segments) =>
      `https://docs.google.com/drawings/d/${segments.join('/')}/preview`,
  },
  'google-drive': {
    ...getGoogleFileConfigs('file', {
      host: ':_domain(docs|drive).google.com',
    }),
    url: (segments) =>
      `https://docs.google.com/file/d/${segments.join('/')}/preview`,
  },
  'google-drive-folder': {
    rules: [
      new UrlRule({
        host: 'drive.google.com',
        path: '/drive{/u/:_uid}?/folders/:id',
      }),
    ],
    fullUrl: ([id]) =>
      id ? `https://drive.google.com/drive/folders/${id}` : '',
    url: ([id]) =>
      id ? `https://drive.google.com/embeddedfolderview?id=${id}#grid` : '',
    defaultAttributes: { height: 'xsmall' },
  },
  'google-form': {
    ...getGoogleFileConfigs('forms'),
    fullUrl: (segments) =>
      `https://docs.google.com/forms/d/${segments.join('/')}/viewform`,
    url: (segments) =>
      `https://docs.google.com/forms/d/${segments.join(
        '/',
      )}/viewform?embedded=true`,
  },
  'google-map': {
    rules: [
      new UrlRule({
        host: 'www.google.com',
        path: '/maps/d{/u/:_uid}?/:_type(embed|viewer)',
        queries: { mid: ':mid' },
      }),
      new UrlRule({
        host: 'www.google.com',
        path: '/maps/embed',
        queries: { pb: ':pb' },
      }),
    ],
    toIdSegments: ({ pb, mid }) =>
      pb
        ? [`pb=${encodeURIComponent(pb)}`]
        : // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
          [`mid=${mid && encodeURIComponent(mid)}`],
    fullUrl: ([id]) => {
      if (!id) {
        return '';
      }
      return id.startsWith('mid=')
        ? `https://www.google.com/maps/d/viewer?${id}`
        : `https://www.google.com/maps/embed?${id}`;
    },
    url: ([id]) => {
      if (!id) {
        return '';
      }
      return id.startsWith('mid=')
        ? `https://www.google.com/maps/d/embed?${id}`
        : `https://www.google.com/maps/embed?${id}`;
    },
  },
  'google-sheet': ((): Rule => {
    const base = getGoogleFileConfigs('spreadsheets', { hash: '{gid=:gid}?' });
    const getHash = ({ gid }: Record<string, string>) =>
      gid ? `#gid=${gid}` : ''; // Sheet id

    return {
      ...base,
      toParams: ({ gid }) => ({ gid }),
      fullUrl: (segments, params) =>
        `${base.fullUrl(segments, params)}${getHash(params)}`,
      readUrl: (segments, params) =>
        `https://docs.google.com/spreadsheets/d/${segments.join(
          '/',
        )}/preview${getHash(params)}`,
      url: (segments, params) =>
        `https://docs.google.com/spreadsheets/d/${segments.join(
          '/',
        )}/edit?rm=minimal${params.gid ? `&gid=${params.gid}` : ''}${getHash(
          params,
        )}`,
    };
  })(),
  'google-slide': ((): Rule => {
    const base = getGoogleFileConfigs('presentation', {
      hash: '{slide=:slide}?',
    });
    const getHash = ({ slide }: Record<string, string>) =>
      slide ? `#slide=${slide}` : '';

    return {
      ...base,
      toParams: ({ slide }) => ({ slide }),
      fullUrl: (segments, params) =>
        `${base.fullUrl(segments, params)}${getHash(params)}`,
      readUrl: (segments, params) =>
        `https://docs.google.com/presentation/d/${segments.join(
          '/',
        )}/embed${getHash(params)}`,
      url: (segments, params) =>
        `https://docs.google.com/presentation/d/${segments.join(
          '/',
        )}/edit?rm=embedded${getHash(params)}`,
    };
  })(),
  iorad: {
    rules: [new UrlRule({ host: 'www.iorad.com', path: '/player/:id(\\d+)' })],
    fullUrl: ([id]) => (id ? `https://www.iorad.com/player/${id}` : ''),
    url: ([id]) =>
      id ? `https://www.iorad.com/player/${id}/?src=iframe&oembed=1` : '',
  },
  invision: {
    rules: [
      new UrlRule({
        host: 'invis.io',
        path: '/:id',
        hash: '/:screenId',
      }),
      new UrlRule({ host: 'invis.io', path: '/:id' }),
      new UrlRule({
        host: ':_type.invisionapp.com',
        path: '/share/:id',
        hash: '/screens/:screenId',
      }),
      new UrlRule({ host: ':_type.invisionapp.com', path: '/share/:id' }),
    ],
    toIdSegments: ({ id, screenId }) =>
      screenId ? [id, screenId.split('_')[0]] : [id],
    fullUrl: ([id, screenId]) => {
      if (!id) {
        return '';
      }
      return screenId
        ? `https://invis.io/${id}/#${screenId}`
        : `https://invis.io/${id}`;
    },
  },
  'invision-v7': {
    rules: [
      new UrlRule({
        host: ':domain.invisionapp.com',
        path: '/console/share/:id/:screenId?',
      }),
    ],
    toIdSegments: ({ domain, id, screenId }) =>
      screenId ? [domain, id, screenId] : [domain, id],
    fullUrl: ([domain, id, screenId]) => {
      const base =
        domain && id
          ? `https://${domain}.invisionapp.com/console/share/${id}`
          : '';
      return screenId ? `${base}/${screenId}` : base;
    },
  },
  jotform: {
    rules: [
      new UrlRule({
        host: ':_(form|www|hipaa).jotform.com',
        path: '/:action(form|jsform|assign)?/:id(\\d+)',
      }),
    ],
    fullUrl: ([id]) => (id ? `https://form.jotform.com/${id}` : ''),
  },
  lucidchart: ((): Rule => {
    const host =
      ':_domain(app\\.lucidchart\\.com|www\\.lucidchart\\.com|lucid\\.app)';
    return {
      rules: [
        new UrlRule({
          host,
          path: '/:_product(lucidchart|lucidspark)/:id(\\w+-\\w+-\\w+-\\w+-\\w+)',
        }),
        new UrlRule({
          host,
          path: '/documents/:_action(edit|embeddedchart|view)/:id',
        }),
      ],
      fullUrl: ([id]) => (id ? `https://lucid.app/documents/edit/${id}` : ''),
      url: ([id]) =>
        id ? `https://lucid.app/documents/embeddedchart/${id}` : '',
    };
  })(),
  ludus: {
    rules: [new UrlRule({ host: 'app.ludus.one', path: '/:id' })],
    fullUrl: ([id]) => (id ? `https://app.ludus.one/${id}/edit` : ''),
    url: ([id]) => (id ? `https://app.ludus.one/${id}` : ''),
  },
  miro: {
    rules: [
      new UrlRule({
        host: 'miro.com',
        path: '/app/:_type(board|live-embed|embed)/:id([^/]+)',
      }),
    ],
    fullUrl: ([id]) => (id ? `https://miro.com/app/board/${id}/` : ''),
    readUrl: ([id]) =>
      id ? `https://miro.com/app/embed/${id}/?autoplay=yep` : '',
    url: ([id], __, { previewMode }) =>
      id
        ? `https://miro.com/app/live-embed/${id}/?${
            previewMode === 'fullScreen'
              ? ''
              : 'embedMode=view_only_without_ui&'
          }embedAutoplay=true`
        : '',
  },
  mode: {
    rules: [
      new UrlRule({
        host: 'app.mode.com',
        path: '/:_(editor)?/:company/reports/:id',
      }),
    ],
    toIdSegments: ({ company, id }) => [company, id],
    fullUrl: ([company, id]) =>
      id && company ? `https://app.mode.com/${company}/reports/${id}` : '',
    url: ([company, id]) =>
      id && company
        ? `https://app.mode.com/${company}/reports/${id}/embed`
        : '',
  },
  pingboard: {
    rules: [
      new UrlRule({
        host: 'app.pingboard.com',
        path: '/s/:id([^/]+)/org_chart',
      }),
    ],
    fullUrl: ([id]) =>
      id ? `https://app.pingboard.com/s/${id}/org_chart` : '',
  },
  slideshare: {
    rules: [
      new UrlRule({
        host: 'www.slideshare.net',
        path: '/slideshow/embed_code/key/:id',
      }),
    ],
    fullUrl: ([id]) =>
      id ? `https://www.slideshare.net/slideshow/embed_code/key/${id}` : '',
  },
  monday: {
    rules: [
      new UrlRule({
        host: ':_(view)?.monday.com',
        path: `/:_type(embed|embed_preview)/:id([\\w]+-[\\w]+)`,
      }),
      new UrlRule({ host: 'view.monday.com', path: '/:id([\\w]+-[\\w]+)' }),
    ],
    fullUrl: ([id]) => (id ? `https://view.monday.com/${id}` : ''),
    url: ([id]) => (id ? `https://view.monday.com/embed/${id}` : ''),
  },
  'microsoft-office': {
    rules: [
      // Personal account
      new UrlRule({
        host: 'onedrive.live.com',
        path: '/embed',
        queries: { resid: ':resid', authkey: ':authkey' },
      }),
      // Business account > personal
      new UrlRule({
        host: ':domain{-my}.sharepoint.com',
        path: '/(:[wxp]:/r)?/personal/:personalName/_layouts/15/Doc.aspx',
        queries: { sourcedoc: ':id' },
      }),
      new UrlRule({
        host: ':domain{-my}.sharepoint.com',
        path: '/(:[wxp]:)/:shareable(g)/personal/:personalName/:id',
      }),
      new UrlRule({
        host: ':domain{-my}.sharepoint.com',
        path: '/(:[wxp]:/r)/personal/:personalName',
        queries: { d: ':existingAccessId(w\\w{32})' },
      }),
      // Business account > non-default site
      new UrlRule({
        host: ':domain.sharepoint.com',
        path: '/(:[wxp]:/r)?/sites/:siteName/_layouts/15/Doc.aspx',
        queries: { sourcedoc: ':id' },
      }),
      new UrlRule({
        host: ':domain.sharepoint.com',
        path: '/(:[wxp]:/r)/sites/:siteName',
        queries: { d: ':existingAccessId(w\\w{32})' },
      }),
      // Business account > default site
      new UrlRule({
        host: ':domain.sharepoint.com',
        path: '/(:[wxp]:/r)?/_layouts/15/Doc.aspx',
        queries: { sourcedoc: ':id' },
      }),
      new UrlRule({
        host: ':domain.sharepoint.com',
        path: '/(:[wxp]:)/:shareable(g)/:id',
      }),
      new UrlRule({
        host: ':domain.sharepoint.com',
        path: '/(:[wxp]:/r)',
        queries: { d: ':existingAccessId(w\\w{32})' },
      }),
    ],
    toIdSegments: ({
      resid,
      authkey,
      domain,
      id,
      siteName,
      personalName,
      shareable,
      existingAccessId,
    }) => {
      const parsedId = existingAccessId
        ? `{${existingAccessId.slice(1, 9)}-${existingAccessId.slice(
            9,
            13,
          )}-${existingAccessId.slice(13, 17)}-${existingAccessId.slice(
            17,
            21,
          )}-${existingAccessId.slice(21, 33)}}`
        : id;

      if (resid && authkey) {
        return [
          `resid=${encodeURIComponent(resid)}&authkey=${encodeURIComponent(
            authkey,
          )}`,
        ];
      }

      if (shareable) {
        if (personalName) {
          return ['shareable', domain, personalName, parsedId];
        }
        return ['shareable', domain, parsedId];
      }

      if (siteName) {
        return [domain, 'sites', siteName, parsedId];
      }

      if (personalName) {
        return [domain, personalName, parsedId];
      }
      return [domain, parsedId];
    },
    fullUrl: (segments) => {
      if (segments[0] === 'shareable') {
        switch (segments.length) {
          case 3: {
            const [domain, id] = segments.slice(1);
            return domain && id
              ? `https://${domain}.sharepoint.com/:w:/g/${id}`
              : '';
          }
          default: {
            const [domain, name, id] = segments.slice(1);
            return domain && id && name
              ? `https://${domain}-my.sharepoint.com/:w:/g/personal/${name}/${id}`
              : '';
          }
        }
      }
      switch (segments.length) {
        case 1: {
          const [query] = segments;
          return query ? `https://onedrive.live.com/embed?${query}&em=2` : '';
        }
        case 2: {
          const [domain, id] = segments;
          return domain && id
            ? `https://${domain}.sharepoint.com/_layouts/15/Doc.aspx?sourcedoc=${id}`
            : '';
        }
        case 3: {
          const [domain, name, id] = segments;
          return domain && id && name
            ? `https://${domain}-my.sharepoint.com/personal/${name}/_layouts/15/Doc.aspx?sourcedoc=${id}`
            : '';
        }
        default: {
          const [domain, sites, name, id] = segments;
          return domain && id && name && sites
            ? `https://${domain}.sharepoint.com/${sites}/${name}/_layouts/15/Doc.aspx?sourcedoc=${id}`
            : '';
        }
      }
    },
    url: (segments) => {
      if (segments[0] === 'shareable') {
        switch (segments.length) {
          case 3: {
            const [domain, id] = segments.slice(1);
            return domain && id
              ? `https://${domain}.sharepoint.com/:w:/g/${id}?action=embedview`
              : '';
          }
          default: {
            const [domain, name, id] = segments.slice(1);
            return domain && id && name
              ? `https://${domain}-my.sharepoint.com/:w:/g/personal/${name}/${id}?action=embedview`
              : '';
          }
        }
      }
      switch (segments.length) {
        case 1: {
          const [query] = segments;
          return query ? `https://onedrive.live.com/embed?${query}&em=2` : '';
        }
        case 2: {
          const [domain, id] = segments;
          return domain && id
            ? `https://${domain}.sharepoint.com/_layouts/15/Doc.aspx?sourcedoc=${id}&action=embedview`
            : '';
        }
        case 3: {
          const [domain, name, id] = segments;
          return domain && id && name
            ? `https://${domain}-my.sharepoint.com/personal/${name}/_layouts/15/Doc.aspx?sourcedoc=${id}&action=embedview`
            : '';
        }
        default: {
          const [domain, sites, name, id] = segments;
          return domain && id && name && sites
            ? `https://${domain}.sharepoint.com/${sites}/${name}/_layouts/15/Doc.aspx?sourcedoc=${id}&action=embedview`
            : '';
        }
      }
    },
  },
  otter: {
    rules: [new UrlRule({ host: 'otter.ai', path: '/u/:id' })],
    toIdSegments: ({ id }) => [id && encodeURIComponent(id)],
    fullUrl: ([id]) => (id ? `https://otter.ai/u/${id}` : ''),
  },
  typeform: {
    rules: [
      new UrlRule({ host: ':_domain.typeform.com', path: '/to/:id' }),
      new UrlRule({
        host: 'admin.typeform.com',
        path: '/form/:id/:section',
      }),
    ],
    toIdSegments: ({ id }) => [id && encodeURIComponent(id)],
    fullUrl: ([id]) => (id ? `https://form.typeform.com/to/${id}` : ''),
  },
  canva: {
    rules: [
      new UrlRule({ host: 'www.canva.com', path: '/design/:id/view' }),
      new UrlRule({ host: 'www.canva.com', path: '/design/:id/:token' }),
    ],
    toIdSegments: ({ id, token }) => [id && encodeURIComponent(id), token],
    fullUrl: ([id, token]) =>
      id
        ? `https://www.canva.com/design/${id}/${token ? `${token}/` : ''}view`
        : '',
    url: ([id, token]) =>
      id
        ? `https://www.canva.com/design/${id}/${
            token ? `${token}/` : ''
          }view?embed`
        : '',
  },
  smartsheet: {
    rules: [
      new UrlRule({
        host: ':domain(app).smartsheet.com',
        path: '/b/publish',
        queries: { EQBCT: ':id' },
      }),
      new UrlRule({ host: ':domain(publish).smartsheet.com', path: '/:id' }),
    ],
    toIdSegments: ({ id, domain }) => [id && encodeURIComponent(id), domain],
    fullUrl: ([id, domain]: IdSegments) => {
      if (!id) {
        return '';
      }
      return domain === 'app'
        ? `https://app.smartsheet.com/b/publish?EQBCT=${id}`
        : `https://publish.smartsheet.com/${id}`;
    },
  },
  twitter: {
    rules: [
      new UrlRule({ host: '(x|twitter).com', path: '/:account/status/:id' }),
      new UrlRule({
        host: ':domain.(x|twitter).com',
        path: '/:account/status/:id',
      }),
    ],
    toIdSegments: ({ id, account }) => [id && encodeURIComponent(id), account],
    fullUrl: ([id, account]) =>
      id && account ? `https://x.com/${account}/status/${id}` : '',
    url: ([id]) =>
      id ? `https://platform.twitter.com/embed/Tweet.html?id=${id}` : '',
  },
  codepen: {
    rules: [
      new UrlRule({ host: 'codepen.io', path: '/:account/pen/:id' }),
      new UrlRule({ host: 'codepen.io', path: '/:account/embed/:id' }),
    ],
    toIdSegments: ({ id, account }) => [id && encodeURIComponent(id), account],
    fullUrl: ([id, account]) =>
      id && account ? `https://codepen.com/${account}/pen/${id}` : '',
    url: ([id, account]) =>
      id && account
        ? `https://codepen.io/${account}/embed/${id}?default-tab=result`
        : '',
  },
  jsfiddle: {
    rules: [
      new UrlRule({ host: 'jsfiddle.net', path: '/:account/:id/:version' }),
      new UrlRule({ host: 'jsfiddle.net', path: '/:accountOrId/:idOrVersion' }),
      new UrlRule({ host: 'jsfiddle.net', path: '/:id' }),
    ],
    toIdSegments: ({ id, account, version, accountOrId, idOrVersion }) => {
      if (accountOrId && idOrVersion) {
        if (Number.isNaN(Number(idOrVersion))) {
          return [idOrVersion, accountOrId];
        }
        return [accountOrId, undefined, idOrVersion];
      }
      if (id && account && version) {
        return [id && encodeURIComponent(id), account, version];
      }
      return [id && encodeURIComponent(id)];
    },

    fullUrl: ([id, account, version]) =>
      id
        ? `https://jsfiddle.net${account ? `/${account}` : ''}/${id}/${
            version ?? ''
          }`
        : '',
    url: ([id, account, version]) =>
      id
        ? `https://jsfiddle.net${account ? `/${account}` : ''}/${id}${
            version ? `/${version}` : ''
          }/embedded/result,js,css,html/`
        : '',
  },
  whimsical: {
    rules: [
      new UrlRule({
        host: 'whimsical.com',
        path: '/:_(embed)?/:nameIdPosition',
      }),
    ],
    toIdSegments: ({ nameIdPosition }) => {
      const idName = nameIdPosition?.split('@')?.[0];
      const id = _.last(idName?.split('-'));
      if (!id || id.length !== 22) {
        return null;
      }
      return [id];
    },
    toParams: ({ nameIdPosition }) => {
      const position = nameIdPosition?.split('@')?.[1];
      return { position };
    },
    fullUrl: ([id], params) =>
      id
        ? `https://whimsical.com/${id}${
            params.position ? `@${params.position}` : ''
          }`
        : '',
    url: ([id], params) =>
      id
        ? `https://whimsical.com/embed/${id}${
            params.position ? `@${params.position}` : ''
          }`
        : '',
  },
  pitch: {
    rules: [
      new UrlRule({
        host: 'app.pitch.com',
        path: '/app/presentation/:accountId/:id',
      }),
      new UrlRule({ host: 'pitch.com', path: '/(embed|public)/:id' }),
    ],
    toIdSegments: ({ id }) => [id],
    toParams: ({ accountId }) => ({ accountId }),
    fullUrl: ([id], { accountId }) => {
      if (!id) {
        return '';
      }
      return accountId
        ? `https://app.pitch.com/app/presentation/${accountId}/${id}`
        : `https://pitch.com/public/${id}`;
    },
    url: ([id]) => (id ? `https://pitch.com/embed/${id}` : ''),
  },
  tableau: {
    rules: [
      new UrlRule({
        host: 'public.tableau.com',
        path: '/views/:id/:sheet',
      }),
      new UrlRule({
        host: 'public.tableau.com',
        path: '/app/profile/:profile/viz/:id/:sheet',
      }),
    ],
    toIdSegments: ({ id }) => [id],
    toParams: ({ sheet, profile }) => ({
      sheet,
      profile,
    }),
    fullUrl: ([id], { sheet, profile }) => {
      if (!id || !sheet) {
        return '';
      }
      if (profile) {
        return `https://public.tableau.com/app/profile/${profile}/viz/${id}/${sheet}`;
      }
      return `https://public.tableau.com/views/${id}/${sheet}`;
    },
    url: ([id], { sheet }) => {
      if (!id || !sheet) {
        return '';
      }
      return `https://public.tableau.com/views/${id}/${sheet}?:showVizHome=no&:embed=true`;
    },
  },
  rows: {
    rules: [
      new UrlRule({
        host: 'rows.com',
        path: '/embed/:id/:element',
      }),
    ],
    toIdSegments: ({ id, element }) => [id, element],
    fullUrl: ([id, element]) => {
      if (!id || !element) {
        return '';
      }
      return `https://rows.com/embed/${id}/${element}`;
    },
    url: ([id, element]) => {
      if (!id || !element) {
        return '';
      }
      return `https://rows.com/embed/${id}/${element}`;
    },
  },
  coda: {
    rules: [
      new UrlRule({
        host: 'coda.io',
        path: '/(d|embed)/:nameAndId/:nameAndPage',
      }),
    ],
    toIdSegments: ({ nameAndId, nameAndPage }) => {
      const id = _.last(nameAndId?.split('_d'));
      const page = _.last(nameAndPage?.split('_'));
      return [id, page];
    },
    fullUrl: ([id, page]) => {
      if (!id || !page) {
        return '';
      }
      return `https://coda.io/d/d${id}/_${page}`;
    },
    url: ([id, page]) => {
      if (!id || !page) {
        return '';
      }
      return `https://coda.io/embed/${id}/_${page}?hideSections=true`;
    },
  },
};

const stringifyParams = (
  params?: Partial<Record<string, string | null>> | null,
): string => {
  if (!params) return '';
  const target: Record<string, string> = {};
  Object.entries(params).forEach(([key, value]) => {
    if (value) {
      target[key] = value;
    }
  });

  return new URLSearchParams(target).toString();
};

function matchEmbed(text: string) {
  const url = safeParseUrl(text);
  if (!url) return false;

  let value: EmbedValue | undefined;
  let attributes: Record<string, unknown> | undefined;
  _.forEach(
    EMBEDS,
    ({ rules, toIdSegments, toParams, defaultAttributes }, type) => {
      firstResult(rules, (rule) => {
        const matched = rule.match(url);
        const idSegments =
          matched && (toIdSegments || (({ id }) => [id]))(matched, url);
        if (idSegments) {
          value = {
            type,
            id: idSegments.join(DELIMITER),
          };

          const params = stringifyParams(matched && toParams?.(matched));
          if (params.length) {
            value.params = params;
          }
          attributes = defaultAttributes;
        }
        return !!matched;
      });
      return value == null;
    },
  );

  if (!value) {
    const matched = matchEntityLinks(url.toString());
    if (matched?.type === 'post') {
      value = matched;
    }
  }

  return value ? { value, attributes } : false;
}

export default matchEmbed;
